import React, { useState } from 'react';
import { Task, TaskStage } from '../../../../types/Task';
import { taskService } from '../../../../services/quantifiedTaskService';
import { useAuth } from '../../../../contexts/AuthContext';
import { Button, Alert, Form } from '@/components/ui';
import ResponsiveModal from '../ResponsiveModal';

interface CancelTaskModalProps {
  visible: boolean;
  onClose: () => void;
  tasks: Task[];
  onSubmit: (tasks: Task[], comments: string) => Promise<void>;
}

const CancelTaskModal: React.FC<CancelTaskModalProps> = ({
  visible,
  onClose,
  tasks,
  onSubmit,
}) => {
  const [comments, setComments] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!comments.trim()) return; // Don't submit if comments are empty
    
    try {
      setLoading(true);
      await onSubmit(tasks, comments);
      onClose();
    } catch (error) {
      console.error('Error cancelling tasks:', error);
      alert('Failed to cancel tasks');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={`Cancel Task${tasks.length > 1 ? 's' : ''}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit}
            disabled={loading || !comments.trim()}
          >
            {loading ? 'Cancelling...' : `Cancel Task${tasks.length > 1 ? 's' : ''}`}
          </Button>
        </>
      }
    >
      <Alert variant="warning">
        <i className="bi bi-exclamation-triangle tw-mr-2"></i>
        Are you sure you want to cancel {tasks.length} task{tasks.length > 1 ? 's' : ''}? This action cannot be undone.
      </Alert>
      <Form.Group className="tw-mb-3">
        <Form.Label>Cancellation Reason <span className="tw-text-danger">*</span></Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="Please provide a reason for cancellation..."
          required
        />
      </Form.Group>
    </ResponsiveModal>
  );
};

export default CancelTaskModal; 